import React from 'react';
import { Skeleton } from '@material-ui/lab';

import AnnouncementCardSkeleton from './SkeletonComponents/AnnouncementCardSkeleton';
import AlertSkeleton from './SkeletonComponents/AlertSkeleton';

import BankDetailsCardSkeleton from './SkeletonComponents/BankDetailsCardSkeleton';
import BannerSkeleton from './SkeletonComponents/BannerSkeleton';
import BottomSheet from './SkeletonComponents/BottomSheetSkeleton';
import BreakupCardSkeleton from './SkeletonComponents/BreakupCardSkeleton';

import CardSkeleton from './SkeletonComponents/CardSkeleton';
import ChatSupportSkeleton from './SkeletonComponents/ChatSupportSkeleton';

import DynamicFormSkeleton from './SkeletonComponents/DynamicFormSkeleton';

import FaqListSkeleton from './SkeletonComponents/FaqListSkeleton';

import HorizontalStepperSkeleton from './SkeletonComponents/HorizontalStepperSkeleton';

import IconTileSkeleton from './SkeletonComponents/IconTileSkeleton';
import ImageCardSkeleton from './SkeletonComponents/ImageCardSkeleton';

import KycCardSkeleton from './SkeletonComponents/KycCardSkeleton';

import ListSkeleton from './SkeletonComponents/ListSkeleton';

import OtpCardSkeleton from './SkeletonComponents/OtpCardSkeleton';
import OrderStatusStepperSkeleton from './SkeletonComponents/OrderStatusStepperSkeleton';

import PaymentOptionSkeleton from './SkeletonComponents/PaymentOptionSkeleton/PaymentOptionSkeleton';
import PaymentOptionsListSkeleton from './SkeletonComponents/PaymentOptionsListSkeleton';
import ProductCardSkeleton from './SkeletonComponents/ProductCardSkeleton';

import QuickLinkSkeleton from './SkeletonComponents/QuickLinkSkeleton';

import RadioGroupSkeleton from './SkeletonComponents/RadioGroupSkeleton';

import SliderWithControlSkeleton from './SkeletonComponents/SliderWithControlSkeleton';
import SocialCardSkeleton from './SkeletonComponents/SocialCardSkeleton';
import SummaryCardSkeleton from './SkeletonComponents/SummaryCardSkeleton';
import StatusCardSkeleton from './SkeletonComponents/StatusCardSkeleton';
import StripSkeleton from './SkeletonComponents/StripSkeleton';
import SummaryProgressCardSkeleton from './SkeletonComponents/SummaryProgressCardSkeleton/SummaryProgressCardSkeleton';
import SwitchTabsSkeleton from './SkeletonComponents/SwitchTabsSkeleton';

import BaseSkeleton from './SkeletonComponents/BaseSkeleton';
import TableSkeleton from './SkeletonComponents/TableSkeleton';
import TabsSkeleton from './SkeletonComponents/TabsSkeleton';
import TestimonialCardSkeleton from './SkeletonComponents/TestimonialCardSkeleton';
import TileSelectionSkeleton from './SkeletonComponents/TileSelectionSkeleton';
import TransactionCardSkeleton from './SkeletonComponents/TransactionCardSkeleton';
import TransactionSummaryCardSkeleton from './SkeletonComponents/TransactionSummaryCardSkeleton';

import VerticalStepperSkeleton from './SkeletonComponents/VerticalStepperSkeleton/VerticalStepperSkeleton';

import { SkeletonUIProps } from './SkeletonUI.types';

const SkeletonUI: React.FC<SkeletonUIProps &
  React.HTMLAttributes<HTMLDivElement>> = ({ variant, options, className }) => {
  switch (variant) {
    case 'announcement-card':
      return (
        <AnnouncementCardSkeleton
          className={className}
          strip={options.strip}
          rightContent={options?.rightContent}
        />
      );
    case 'alert':
      return (
        <AlertSkeleton
          className={className} />
      );
    case 'bank-details-card':
      return <BankDetailsCardSkeleton className={className} />;
    case 'banner':
        return <BannerSkeleton className={className} strip={options?.strip} />;
    case 'bottom-sheet':
        return <BottomSheet />;
    case 'breakup-card':
      return (
        <BreakupCardSkeleton
          className={className}
          size={options?.size}
          showButton={options?.showButton}
        />
      );
    case 'card':
      return (
        <CardSkeleton
          size={options?.size}
          separator={options?.separator}
          className={className}
        />
      );
    case 'chat-support':
      return <ChatSupportSkeleton className={className} />;
    case 'dynamic-form':
      return <DynamicFormSkeleton className={className} />;
    case 'faq-list':
      return <FaqListSkeleton className={className} size={options?.size} />;
    case 'horizontal-stepper':
      return <HorizontalStepperSkeleton className={className} steps={options?.steps} />;
    case 'icon-tile':
      return <IconTileSkeleton className={className} />;
    case 'image-card':
      return <ImageCardSkeleton className={className} />;
    case 'kyc-card':
      return <KycCardSkeleton className={className} />;
    case 'list':
        return <ListSkeleton className={className} list={options.list}/>;
    case 'otp-card':
      return <OtpCardSkeleton className={className} />;
    case 'order-status-stepper':
      return <OrderStatusStepperSkeleton className={className} />;
    case 'payment-option':
      return <PaymentOptionSkeleton className={className} />;
    case 'payment-options-list':
      return (
        <PaymentOptionsListSkeleton
          size={options?.size}
        />
      );
    case 'product-card':
      return <ProductCardSkeleton className={className} icon={options?.icon} />;
    case 'quick-link':
      return <QuickLinkSkeleton className={className} list={options?.list} />;
    case 'radio-group':
      return <RadioGroupSkeleton className={className} />;
    case 'skeleton':
      return (
        <Skeleton
          variant={options?.variant ?? 'rect'}
          width={options?.width ?? 100}
          height={options?.height ?? 100}
          className={className}
        />
      );
    case 'slider-with-control':
      return <SliderWithControlSkeleton className={className} />;
    case 'social-card':
      return <SocialCardSkeleton className={className} />;
    case 'status-card':
      return (
        <StatusCardSkeleton
          className={className}
          showButton={options?.showButton}
        />
      );
    case 'strip':
      return (
        <StripSkeleton />
      );
    case 'summary-card':
      return <SummaryCardSkeleton className={className} />;
    case 'summary-progress-card':
      return <SummaryProgressCardSkeleton className={className} />;
    case 'switch-tabs':
      return <SwitchTabsSkeleton className={className} />;
    case 'table':
      return (
        <TableSkeleton
          rows={options?.rows}
          columns={options?.columns}
          textSize={options?.textSize}
          highlightColumn={options?.highlightColumn}
          highlightRow={options?.highlightRow}
          className={className}
        />
      );
    case 'tabs':
      return <TabsSkeleton className={className} size={options?.size} />;
    case 'testimonial-card':
      return <TestimonialCardSkeleton className={className} />;
    case 'base':
      return (
        <BaseSkeleton
          width={options?.width}
          height={options?.height}
          variant={options?.variant}
          className={className}
        />
      );
    case 'tile-selection':
      return <TileSelectionSkeleton className={className} />;
    case 'transaction-card':
      return <TransactionCardSkeleton className={className} />;
    case 'transaction-summary-card':
      return <TransactionSummaryCardSkeleton className={className} />;
    case 'vertical-stepper':
      return (
        <VerticalStepperSkeleton className={className} steps={options?.steps} />
      );

    default:
      return null;
  }
};

export default SkeletonUI;
